<template>
    <transition name="panel-fade">
        <div v-if="show" class="sub-overlay" @click.self="close">
            <transition name="panel-slide">
                <aside v-if="show" class="sub-panel">
                    <div class="sub-panel-head">
                        <div>
                            <h2>Generate Payment Link</h2>
                            <p>Create a Razorpay purchase link for a new prospect</p>
                        </div>
                        <button class="sub-close" @click="close">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                            </svg>
                        </button>
                    </div>

                    <div class="sub-panel-body">
                        <div v-if="!purchaseUrl">
                            <div class="sub-section">
                                <label class="sub-label">Company Name</label>
                                <input type="text" v-model="companyName" class="form-input-premium" placeholder="e.g., Acme Constructions">
                            </div>

                            <div class="sub-section">
                                <label class="sub-label">Admin Contact Email</label>
                                <input type="email" v-model="adminContactEmail" class="form-input-premium" placeholder="admin@company.com">
                            </div>

                            <div class="sub-section">
                                <label class="sub-label">Plan</label>
                                <select v-model="selectedPlanId" class="form-input-premium">
                                    <option :value="null" disabled>Choose a plan...</option>
                                    <option v-for="plan in activePlans" :key="plan.id" :value="plan.id">
                                        {{ plan.name }} — ₹{{ formatPrice(plan.price) }} / {{ formatDuration(plan.duration_days) }}
                                    </option>
                                </select>
                            </div>

                            <div v-if="message" class="sub-message" :class="messageType">{{ message }}</div>

                            <button class="btn-assign" :disabled="!canGenerate || generating" @click="generateLink">
                                <span v-if="generating" class="spinner-small"></span>
                                {{ generating ? 'Generating...' : 'Generate Link' }}
                            </button>
                        </div>

                        <div v-else class="sub-section">
                            <label class="sub-label">Purchase Link</label>
                            <div class="link-box">
                                <input type="text" readonly :value="purchaseUrl" class="form-input-premium link-input">
                                <button class="btn-copy" @click="copyLink">{{ copied ? 'Copied!' : 'Copy' }}</button>
                            </div>
                            <p class="field-hint">Share this link with the prospect via WhatsApp or email.</p>
                            <button class="btn-assign" @click="reset">Generate Another Link</button>
                        </div>
                    </div>
                </aside>
            </transition>
        </div>
    </transition>
</template>

<script>
import axios from 'axios';

export default {
    name: 'PaymentLinkPanel',
    props: {
        show: { type: Boolean, default: false },
    },
    emits: ['close', 'created'],
    data() {
        return {
            plans: [],
            companyName: '',
            adminContactEmail: '',
            selectedPlanId: null,
            generating: false,
            message: '',
            messageType: 'success',
            purchaseUrl: '',
            copied: false,
        }
    },
    computed: {
        activePlans() {
            return this.plans.filter(p => p.is_active);
        },
        canGenerate() {
            return this.companyName.trim() && this.adminContactEmail.trim() && this.selectedPlanId;
        }
    },
    watch: {
        show(val) {
            if (val) {
                this.reset();
                this.fetchPlans();
            }
        }
    },
    methods: {
        authHeaders() {
            return { Authorization: `Bearer ${localStorage.getItem('super_admin_token')}` };
        },
        close() {
            this.$emit('close');
        },
        reset() {
            this.companyName = '';
            this.adminContactEmail = '';
            this.selectedPlanId = null;
            this.message = '';
            this.purchaseUrl = '';
            this.copied = false;
        },
        async fetchPlans() {
            try {
                const response = await axios.get(`${this.$API_URL}/super_admin/plans?page_size=100`, {
                    headers: this.authHeaders()
                });
                this.plans = response.data.items || [];
            } catch (error) {
                console.error('Error fetching plans:', error);
            }
        },
        formatPrice(price) {
            return (price || 0).toLocaleString('en-IN');
        },
        formatDuration(days) {
            if (days === 30) return 'month';
            if (days === 90) return 'quarter';
            if (days === 365) return 'year';
            return `${days} days`;
        },
        async generateLink() {
            if (!this.canGenerate || this.generating) return;
            this.generating = true;
            this.message = '';
            try {
                const response = await axios.post(`${this.$API_URL}/payment/link`, {
                    company_name: this.companyName.trim(),
                    admin_contact_email: this.adminContactEmail.trim(),
                    plan_id: this.selectedPlanId,
                }, {
                    headers: this.authHeaders()
                });
                this.purchaseUrl = response.data.purchase_url;
                this.$emit('created');
            } catch (error) {
                this.messageType = 'error';
                this.message = error.response?.data?.message || 'Failed to generate payment link';
            } finally {
                this.generating = false;
            }
        },
        async copyLink() {
            try {
                await navigator.clipboard.writeText(this.purchaseUrl);
                this.copied = true;
                setTimeout(() => { this.copied = false; }, 2000);
            } catch (error) {
                console.error('Copy failed:', error);
            }
        }
    }
}
</script>

<style scoped>
.sub-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.5);
    backdrop-filter: blur(2px);
    z-index: 1200;
    display: flex;
    justify-content: flex-end;
}

.sub-panel {
    width: 100%;
    max-width: 420px;
    height: 100%;
    background: white;
    box-shadow: -10px 0 40px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    font-family: var(--font-sans);
}

.sub-panel-head {
    padding: 1.75rem 1.75rem 1.25rem;
    border-bottom: 1px solid #eef2f7;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
}

.sub-panel-head h2 {
    margin: 0;
    font-family: var(--font-display);
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--text-primary);
}

.sub-panel-head p {
    margin: 0.35rem 0 0;
    font-size: 0.85rem;
    color: var(--text-secondary);
}

.sub-close {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    color: var(--text-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
}

.sub-close:hover { background: #f1f5f9; color: #ef4444; border-color: #fecaca; }

.sub-panel-body {
    flex: 1;
    overflow-y: auto;
    padding: 1.75rem;
    display: flex;
    flex-direction: column;
    gap: 1.75rem;
}

.sub-section { display: flex; flex-direction: column; gap: 0.6rem; margin-bottom: 1.5rem; }

.sub-label {
    font-size: var(--fs-label);
    font-weight: var(--fw-label);
    letter-spacing: var(--tracking-label);
    text-transform: uppercase;
    color: var(--text-label);
}

.form-input-premium {
    width: 100%;
    padding: 0.85rem 1rem;
    border: 1.5px solid #e2e8f0;
    border-radius: 12px;
    font-size: 0.9rem;
    font-family: var(--font-sans);
    outline: none;
    transition: all 0.2s;
    box-sizing: border-box;
    background: white;
    color: var(--text-primary);
}

.form-input-premium:focus {
    border-color: #0071C5;
    box-shadow: 0 0 0 4px rgba(0, 113, 197, 0.1);
}

.field-hint {
    margin: 0;
    font-size: 0.75rem;
    color: #94a3b8;
    font-weight: 500;
}

.btn-assign {
    width: 100%;
    margin-top: 0.25rem;
    padding: 0.8rem;
    border: none;
    border-radius: 12px;
    background: linear-gradient(90deg, #0071C5, #0084FF);
    color: white;
    font-weight: 700;
    font-family: var(--font-sans);
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 6px 16px rgba(0, 113, 197, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.btn-assign:hover:not(:disabled) { transform: translateY(-1px); }
.btn-assign:disabled { opacity: 0.5; cursor: not-allowed; }

.link-box {
    display: flex;
    gap: 0.6rem;
}

.link-input { flex: 1; font-size: 0.8rem; color: var(--text-secondary); }

.btn-copy {
    padding: 0 1.3rem;
    border: none;
    border-radius: 12px;
    background: #10b981;
    color: white;
    font-weight: 700;
    font-family: var(--font-sans);
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
}

.btn-copy:hover { background: #059669; }

.sub-message {
    padding: 0.75rem 1rem;
    border-radius: 10px;
    font-size: 0.85rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
}

.sub-message.success { background: #dcfce7; color: #166534; }
.sub-message.error { background: #fee2e2; color: #991b1b; }

.spinner-small {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.panel-fade-enter-active, .panel-fade-leave-active { transition: opacity 0.25s ease; }
.panel-fade-enter-from, .panel-fade-leave-to { opacity: 0; }

.panel-slide-enter-active { transition: transform 0.3s cubic-bezier(0.2, 0.8, 0.2, 1); }
.panel-slide-leave-active { transition: transform 0.25s ease; }
.panel-slide-enter-from, .panel-slide-leave-to { transform: translateX(100%); }
</style>
